"use client";

import { dashboardPathHref } from '@/lib/routing/platform-hosts';

import Link from "next/link";
import { ArrowRight, Compass, Home, LayoutTemplate, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export default function NotFound() {
  const { user } = useAuth();

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,90,60,0.12),_transparent_42%),radial-gradient(circle_at_85%_15%,_rgba(15,15,15,0.08),_transparent_36%)]" />

      <main className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <section className="w-full max-w-4xl overflow-hidden rounded-[var(--pub-r-xl)] border border-border/80 bg-card/90 shadow-[0_20px_70px_rgba(0,0,0,0.15)] backdrop-blur">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-accent/70 to-transparent" />

          <div className="space-y-8 p-6 sm:p-10 lg:p-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-muted/60 px-3 py-1 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
              <SearchX className="h-3.5 w-3.5" />
              Error 404
            </div>

            <div className="space-y-4">
              <h1 className="text-[clamp(3rem,11vw,7.5rem)] font-semibold leading-[0.92] tracking-[-0.04em]">
                Page Not Found
              </h1>
              <p className="max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
                The page you requested does not exist, may have moved, or you might not have access.
                Continue with one of the routes below.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button size="lg" asChild className="gap-2">
                <Link href={user ? dashboardPathHref('/dashboard') : "/"} suppressHydrationWarning>
                  <Home className="h-4 w-4" />
                  <span suppressHydrationWarning>{user ? "Go to dashboard" : "Back to home"}</span>
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="gap-2">
                <Link href="/product">
                  Explore product
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Link
                href="/solutions"
                className="group rounded-[var(--pub-r-md)] border border-border/70 bg-muted/30 px-4 py-4 transition-colors hover:bg-muted/60"
              >
                <div className="mb-2 inline-flex rounded-[var(--pub-r-sm)] bg-background p-2">
                  <Compass className="h-4 w-4 text-accent" />
                </div>
                <p className="text-sm font-semibold">Explore solutions</p>
                <p className="mt-1 text-xs text-muted-foreground">See how Menuraq supports food-business workflows.</p>
              </Link>

              <Link
                href={user ? dashboardPathHref('/dashboard') : "/auth/signin"}
                className="group rounded-[var(--pub-r-md)] border border-border/70 bg-muted/30 px-4 py-4 transition-colors hover:bg-muted/60"
              >
                <div className="mb-2 inline-flex rounded-[var(--pub-r-sm)] bg-background p-2">
                  <LayoutTemplate className="h-4 w-4 text-accent" />
                </div>
                <p className="text-sm font-semibold" suppressHydrationWarning>{user ? "Go to dashboard" : "Sign in to dashboard"}</p>
                <p className="mt-1 text-xs text-muted-foreground" suppressHydrationWarning>
                  {user ? "Access your menus, templates, and analytics." : "Access menus, templates, and analytics."}
                </p>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
